import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { History } from 'lucide-react';
import { useMemo } from 'react';
import { useI18n } from '@/providers/i18n';
import { useModelLabelById } from '@/lib/models/use-model-label';

type HistoryRecord = {
  id: string;
  url?: string;
  urls?: string[];
  modelId?: string;
  /** 可选：直接存人类可读的模型名（优先展示） */
  modelLabel?: string;
  createdAt: string;
};

type HistoryItemProps = {
  items?: HistoryRecord[];
  activeId?: string;
  onSelect?: (record: HistoryRecord) => void;
  disabled?: boolean;
  showPreview?: boolean;
};

export function HistoryItem({ items, activeId, onSelect, disabled, showPreview = true }: HistoryItemProps) {
  const { t } = useI18n();
  const getModelLabelById = useModelLabelById();
  // Newest first. Memoized so we don't allocate a reversed copy on every parent
  // toolbar re-render (which happens on most node-data changes).
  const list = useMemo(
    () => (Array.isArray(items) ? items.slice().reverse() : []),
    [items],
  );

  // Always render a (disabled) History button even when there is no history,
  // so toolbars stay visually stable and users can discover the feature.
  if (list.length === 0) {
    return (
      <Button size="sm" variant="ghost" className="rounded-md h-8 px-2 text-xs" disabled>
        <History size={12} className="mr-1" />
        {t('History')}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" className="rounded-md h-8 px-2 text-xs" disabled={disabled}>
          <History size={12} className="mr-1" />
          {t('History')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {list.map((record) => {
          const isActive = Boolean(activeId && record.id === activeId);
          return (
            <DropdownMenuItem
              key={record.id}
              disabled={disabled || isActive}
              onClick={() => {
                if (isActive) return;
                onSelect?.(record);
              }}
              className={`flex items-center gap-2 ${isActive ? 'bg-muted/50' : ''}`}
            >
              {showPreview ? (
                <div className="w-8 h-8 rounded overflow-hidden bg-muted">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={record.url || (Array.isArray(record.urls) ? record.urls[0] ?? '' : '')}
                    alt={t('History preview')}
                    width={32}
                    height={32}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      try {
                        (e.target as HTMLImageElement).style.visibility = 'hidden';
                      } catch {}
                    }}
                  />
                </div>
              ) : null}
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground truncate">{new Date(record.createdAt).toLocaleString()}</div>
                <div className="text-xs font-medium truncate">
                  {record.modelLabel || getModelLabelById(record.modelId) || record.modelId || t('Previous generation')}
                </div>
              </div>
              {isActive ? (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                  {t('Current')}
                </span>
              ) : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


